import { Controller } from 'react-hook-form'
import { Box, FormLabel, Tooltip } from '@mui/material'
import { TAttribute } from 'types'
import { TRule } from 'utils'
import { getRangeTooltipMessage } from 'utils/helpers/validationFunctions'
// This style should not be imported from a specific
// page or feature into a generic ParameterRange component
import { CustomTextField } from '../../../pages/Settings/Products/shared/dialogs/dialogs.styled'
import { InfoItem } from './Parameters.styled'
import * as sxStyles from './Parameters.styled'

interface IParameterRangeProps {
  attribute: TAttribute
  control: any
  attributeIndex: number
  nameMin?: string
  nameMax?: string
  warningMinMessage?: string
  warningMaxMessage?: string
  validationRules?: Record<string, TRule>
}

export const ParameterRange: React.FC<IParameterRangeProps> = ({
  attribute,
  control,
  attributeIndex,
  nameMin,
  nameMax,
  warningMinMessage,
  warningMaxMessage,
  validationRules,
}) => {
  const getFieldName = (type: 'range_min' | 'range_max') =>
    type === 'range_min'
      ? nameMin || `attributes.${attributeIndex}.range_min`
      : nameMax || `attributes.${attributeIndex}.range_max`

  const handleBlurSanitize = (value: string | number) => {
    const parsed = parseFloat(String(value).trim())
    return isNaN(parsed) ? 0 : parsed
  }

  return (
    <InfoItem key={attribute?.name}>
      <FormLabel required={attribute?.is_mandatory}>
        {attribute?.name}
      </FormLabel>
      <Box sx={sxStyles.inputBoxWrapper}>
        <Box sx={sxStyles.valueBoxWrapper}>
          <Controller
            name={getFieldName('range_min')}
            control={control}
            defaultValue={attribute?.range_min || ''}
            render={({ field, fieldState: { error } }) => (
              <Tooltip
                title={getRangeTooltipMessage(
                  validationRules,
                  attribute.name,
                  'range_min'
                )}
                placement="top"
                arrow
              >
                <CustomTextField
                  {...field}
                  size="small"
                  fullWidth
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value)}
                  onBlur={(e) => {
                    const sanitized = handleBlurSanitize(e.target.value)
                    field.onChange(sanitized)
                  }}
                  error={!!error}
                  helperText={error?.message || warningMinMessage || ''}
                  sx={
                    warningMinMessage
                      ? sxStyles.rightHelperWarningTextStyle
                      : sxStyles.rightHelperTextStyle
                  }
                />
              </Tooltip>
            )}
          />
        </Box>
        <Box sx={sxStyles.dashWrapperBox}>-</Box>
        <Box sx={sxStyles.valueBoxWrapper}>
          <Controller
            name={getFieldName('range_max')}
            control={control}
            defaultValue={attribute?.range_max || ''}
            render={({ field, fieldState: { error } }) => (
              <Tooltip
                title={getRangeTooltipMessage(
                  validationRules,
                  attribute.name,
                  'range_max'
                )}
                placement="top"
                arrow
              >
                <CustomTextField
                  {...field}
                  size="small"
                  fullWidth
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value)}
                  onBlur={(e) => {
                    const sanitized = handleBlurSanitize(e.target.value)
                    field.onChange(sanitized)
                  }}
                  error={!!error}
                  helperText={error?.message || warningMaxMessage || ''}
                  sx={
                    warningMaxMessage
                      ? sxStyles.leftHelperWarningTextStyle
                      : sxStyles.leftHelperTextStyle
                  }
                />
              </Tooltip>
            )}
          />
        </Box>
      </Box>
    </InfoItem>
  )
}
